export class Tabs {

    static #instances = new Map();
    static #globalEventListenersAdded = false;

    static {
        if (!Tabs.#globalEventListenersAdded) {
            Tabs.#addGlobalEventListeners();
            Tabs.#globalEventListenersAdded = true;
        }
    }

    constructor(settings = {}) {
        const {
            tabs,
            allowDeeplinking = false,
            on = {}
        } = settings;

        if (typeof tabs === 'string') {
            this.tabs = document.querySelector(tabs);
        } else if (tabs instanceof HTMLElement) {
            this.tabs = tabs;
        } else {
            console.warn('Invalid tabs parameter. Expected string or HTMLElement.');
            return;
        }

        if (!this.tabs) {
            console.warn(`Tabs element not found for selector or element: ${tabs}`);
            return;
        }

        if (Tabs.#instances.has(this.tabs)) {
            console.warn('Tabs instance already exists for the provided element.');
            return;
        }

        this.allowDeeplinking = allowDeeplinking;
        this.on = {
            change: on.change || (() => {})
        };
        this.isTriggered = false;

        // Init
        Tabs.#instances.set(this.tabs, this);
        this.tabs.classList.add('no-transition');
        this.init();
        requestAnimationFrame(() => {
            this.tabs.classList.remove('no-transition');
        });

        Tabs.#historyStateHandler();

    }

    init() {
        this.tabButtons = Array.from(this.tabs.querySelectorAll('[role="tab"]'));
        this.tabPanels = this.tabs.querySelectorAll('[role="tabpanel"]');

        this.tabButtons.forEach(button => {
            button.addEventListener('click', (event) => this.changeTab(event.currentTarget));
        });

        const activeTab = this.tabs.querySelector('[role="tab"][aria-selected="true"]');
        if (activeTab) {
            this.changeTab(activeTab, false);
        }
    }

    changeTab(newTab, triggerEvent = true) {
        if (!newTab || !newTab.matches('[role="tab"]')) return;

        const activeTab = this.tabs.querySelector('[role="tab"][aria-selected="true"]');
        const newPanel = this.tabs.querySelector(`#${newTab.getAttribute('aria-controls')}`);
        const activePanel = activeTab ? this.tabs.querySelector(`#${activeTab.getAttribute('aria-controls')}`) : null;

        if (activeTab === newTab) return;

        if (activeTab) {
            activeTab.setAttribute('aria-selected', 'false');
            activeTab.setAttribute('tabindex', '-1');
            if (activePanel) {
                activePanel.setAttribute('aria-hidden', 'true');
                activePanel.hidden = true;
                activePanel.setAttribute('tabindex', '-1');
            }
        }

        newTab.setAttribute('aria-selected', 'true');
        newTab.setAttribute('tabindex', '0');

        if (newPanel) {
            newPanel.removeAttribute('aria-hidden');
            newPanel.hidden = false;
            newPanel.setAttribute('tabindex', '0');
        }

        if (triggerEvent && typeof this.on.change === 'function') {
            this.on.change.call(this, activePanel, newPanel);
        }

        this.setHistoryState(newTab);
    }

    setHistoryState(activeTab) {
        if (window.HashManager && this.allowDeeplinking) {
            const identifier = encodeURIComponent(activeTab.id || this.tabButtons.indexOf(activeTab).toString());
            if (this.tabs.id) {
                window.HashManager.updateState({
                    [this.tabs.id]: identifier
                });
            }
        }
    }

    static #addGlobalEventListeners() {
        window.addEventListener('popstate', Tabs.#debounce(Tabs.#historyStateHandler, 100));
    }

    static #historyStateHandler() {
        if (window.HashManager) {
            const states = window.HashManager.state;
            Tabs.#instances.forEach(instance => {
                if (!instance.isTriggered && instance.allowDeeplinking) {
                    if (Object.prototype.hasOwnProperty.call(states, instance.tabs.id)) {
                        const identifier = states[instance.tabs.id];
                        const tabToOpen = instance.tabButtons.find(tab => tab.id === identifier || instance.tabButtons.indexOf(tab).toString() === identifier);
                        if (tabToOpen) {
                            instance.changeTab(tabToOpen, false);
                        } else {
                            console.warn(`Tab with identifier "${identifier}" not found.`);
                        }
                    }
                }
                if (instance.isTriggered) instance.isTriggered = false;
            });
        }
    }

    static #debounce(func, delay) {
        let timer;
        return function (...args) {
            clearTimeout(timer);
            timer = setTimeout(() => func.apply(this, args), delay);
        };
    }
}
